/**
 * The live client. Real API, real bearer token, real refusals.
 *
 * Thin by the same rule as the fleet client: every method asks the API a
 * question and returns the answer. Derivation happens in `models.ts`, once.
 */

import { request, upload } from '../../api/client';
import type {
  CallSheetExtraction, Confirmation, DaySuggestion, DocumentSummary,
  EquipmentClass, Estimate, ExtractionAttempt, Production, ProductionSummary,
  RegisteredDocument, ShootDay,
} from './models';
import type { ModellerService } from './service';

/** The wire shape of one day suggestion, before the two facts are grouped. */
interface DaySuggestionWire {
  id: number;
  day_number: number | null;
  local_date: string | null;
  raw_unit_label: string | null;
  day_identifier_raw: string | null;
  evidence_quote: string | null;
  page_no: number | null;
  segment_count: number;
  crew_call_local: string | null;
  crew_call_fact_id: number | null;
  crew_call_fact_status: DaySuggestion['crew_call']['status'];
  crew_call_resolution_note: string | null;
  crew_call_candidates: string[];
  crew_call_status: DaySuggestion['crew_call']['confirmation_status'];
  crew_call_confirmation_id: number | null;
  estimated_wrap_local: string | null;
  estimated_wrap_fact_id: number | null;
  estimated_wrap_fact_status: DaySuggestion['estimated_wrap']['status'];
  estimated_wrap_resolution_note: string | null;
  estimated_wrap_candidates: string[];
  estimated_wrap_status: DaySuggestion['estimated_wrap']['confirmation_status'];
  estimated_wrap_confirmation_id: number | null;
}

/**
 * The response carries the two facts as flat prefixed columns. Grouping them
 * here keeps the PARSER state and the HUMAN state visibly separate in every
 * component that touches them — `fact.status` and `fact.confirmation_status`
 * answer different questions and must not collapse into one chip.
 */
function groupFacts(w: DaySuggestionWire): DaySuggestion {
  return {
    id: w.id,
    day_number: w.day_number,
    local_date: w.local_date,
    raw_unit_label: w.raw_unit_label,
    day_identifier_raw: w.day_identifier_raw,
    evidence_quote: w.evidence_quote,
    page_no: w.page_no,
    segment_count: w.segment_count,
    crew_call: {
      fact_id: w.crew_call_fact_id,
      status: w.crew_call_fact_status,
      value: w.crew_call_local,
      raw_value_text: null,
      resolution_note: w.crew_call_resolution_note,
      candidates: w.crew_call_candidates ?? [],
      confirmation_status: w.crew_call_status,
      confirmation_id: w.crew_call_confirmation_id,
    },
    estimated_wrap: {
      fact_id: w.estimated_wrap_fact_id,
      status: w.estimated_wrap_fact_status,
      value: w.estimated_wrap_local,
      raw_value_text: null,
      resolution_note: w.estimated_wrap_resolution_note,
      candidates: w.estimated_wrap_candidates ?? [],
      confirmation_status: w.estimated_wrap_status,
      confirmation_id: w.estimated_wrap_confirmation_id,
    },
  };
}

export const liveService: ModellerService = {
  mode: 'live',

  productions: () => request<ProductionSummary[]>('/v1/productions'),

  production: (slug) =>
    request<Production>(`/v1/productions/${encodeURIComponent(slug)}`),

  equipment: () => request<EquipmentClass[]>('/v1/equipment'),

  estimates: (slug) =>
    request<Estimate[]>(`/v1/productions/${encodeURIComponent(slug)}/estimates`),

  estimate: (id) => request<Estimate>(`/v1/estimates/${id}`),

  daySuggestions: async (extractionId) =>
    (await request<DaySuggestionWire[]>(`/v1/extractions/${extractionId}/shoot-days`))
      .map(groupFacts),

  createProduction: (body) =>
    request<Production>('/v1/productions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  documents: (slug) =>
    request<DocumentSummary[]>(`/v1/productions/${encodeURIComponent(slug)}/documents`),

  documentExtractions: (slug, documentId) =>
    request<ExtractionAttempt[]>(
      `/v1/productions/${encodeURIComponent(slug)}/documents/${documentId}/extractions`),

  createShootDay: (slug, body) =>
    request<ShootDay>(`/v1/productions/${encodeURIComponent(slug)}/shoot-days`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  registerDocument: (slug, body) =>
    request<RegisteredDocument>(`/v1/productions/${encodeURIComponent(slug)}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  extractCallSheet: (slug, documentId, file, requestedBy) => {
    const form = new FormData();
    form.append('file', file, file.name);
    /* `requested_by` is a QUERY parameter on this route, not a form field —
       the route signature declares `Query(min_length=1)`. Sending it in the
       body would 422 with a message about a missing query parameter. */
    return upload<CallSheetExtraction>(
      `/v1/productions/${encodeURIComponent(slug)}/documents/${documentId}`
      + `/call-sheet-extractions?requested_by=${encodeURIComponent(requestedBy)}`,
      form);
  },

  confirmFact: (factId, body) =>
    request<Confirmation>(`/v1/extraction-shoot-day-facts/${factId}/confirm`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
